<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import {
  fetchGhlPaymentOrderData,
  submitGhlPaymentCheckout
} from '@/api/ghlPayment.api';
import { redirectToPayDollar as performPayDollarRedirect } from '@/utils/paydollarRedirect';
import { getCurrentUser } from '@/utils/index';
import {
  PH_COUNTRY_ID,
  emptyAddress,
  fetchCountries,
  fetchZones,
  findCountryId,
  findZoneId,
  toPositiveInt,
  zoneName
} from '@/utils/checkoutAddress';

const route = useRoute();

const loading = ref(true);
const submitting = ref(false);
const errorMessage = ref('');
const paymentInitiate = ref(null);
const productDetails = ref([]);
const hasInsuranceCoverage = ref(false);
const paymentStatus = ref('');

const addressForm = ref(emptyAddress());
const shippingForm = ref(emptyAddress());
const sameShippingAsBilling = ref(true);
const countries = ref([]);
const billingZones = ref([]);
const shippingZones = ref([]);
const pendingZoneHint = {
  billing: '',
  shipping: ''
};

const insuranceForm = ref({
  fname: '',
  mname: '',
  lname: '',
  dob: '',
  nationality: 'Philippines',
  jobClassification: '1',
  jobClassificationText: '',
  contact: '',
  email: '',
  street: '',
  country: 'Philippines',
  state: '',
  postalCode: '',
  municipality: '',
  address_country: 0,
  address_province: 0,
  address_country2: 0,
  benefFname: '',
  benefMname: '',
  benefLname: '',
  benefDob: '',
  benefContact: '',
  benefRelationship: ''
});

const READY_RETRY_MS = 500;
const READY_MAX_ATTEMPTS = 30;
const READY_PAYLOAD = { "type": 'custom_provider_ready', "loaded": true };

let readyIntervalId = null;

const locationId = computed(() => {
  const pi = paymentInitiate.value;
  return route.query.locationId || (pi && pi.locationId) || '';
});
const isTerminalStatus = computed(() => ['success', 'fail', 'cancel'].includes(paymentStatus.value));

const formattedAmount = computed(() => {
  const pi = paymentInitiate.value;
  const amount = Number((pi && pi.amount) || 0);
  return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount);
});

const paymentContact = computed(() => {
  const pi = paymentInitiate.value;
  return pi && pi.contact ? pi.contact : null;
});

function hasProductPrice(item) {
  return Boolean(item && item.prices && item.prices.length);
}

function formatProductPrice(item) {
  if (!hasProductPrice(item)) return '';
  const amount = item.prices[0].amount;
  if (amount == null) return '';
  return `PHP ${Number(amount).toLocaleString()}`;
}

function fillAddressIfEmpty(target, source, hintKey) {
  if (!source) return;
  if (!target.address1 && source.address1) target.address1 = source.address1;
  if (!target.city && source.city) target.city = source.city;
  if (!target.postcode && source.postcode) target.postcode = source.postcode;

  const countryId = findCountryId(countries.value, source.countryId || source.country);
  if (countryId && (!target.countryId || target.countryId === PH_COUNTRY_ID)) {
    target.countryId = countryId;
  }

  const zoneId = toPositiveInt(source.zoneId);
  if (zoneId && !target.zoneId) {
    target.zoneId = zoneId;
    pendingZoneHint[hintKey] = '';
  } else if (source.state && !target.zoneId && !pendingZoneHint[hintKey]) {
    pendingZoneHint[hintKey] = source.state;
  }
}

function mapIncomingAddress(address) {
  if (!address || typeof address !== 'object') return emptyAddress();
  return {
    address1: String(address.address1 || address.line1 || address.street || '').trim(),
    city: String(address.city || address.municipality || '').trim(),
    postcode: String(address.postcode || address.zipCode || address.postalCode || '').trim(),
    countryId: toPositiveInt(address.country_id || address.countryId),
    country: String(address.country || '').trim(),
    zoneId: toPositiveInt(address.zone_id || address.zoneId),
    state: String(address.state || '').trim()
  };
}

function loadLoggedInUserAddress() {
  const user = getCurrentUser();
  if (!user) return emptyAddress();
  return {
    address1: String(user.address || '').trim(),
    city: String(user.city || '').trim(),
    postcode: String(user.postcode || user.postalCode || '').trim(),
    countryId: toPositiveInt(user.country_id || user.countryId),
    zoneId: toPositiveInt(user.zone_id || user.zoneId),
    state: String(user.state || '').trim()
  };
}

function selectedStateName(form, zones) {
  return zoneName(zones, form.zoneId);
}

function syncAddressToInsurance() {
  if (addressForm.value.address1) insuranceForm.value.street = addressForm.value.address1;
  if (addressForm.value.city) insuranceForm.value.municipality = addressForm.value.city;
  const state = selectedStateName(addressForm.value, billingZones.value);
  if (state) insuranceForm.value.state = state;
  if (addressForm.value.postcode) insuranceForm.value.postalCode = addressForm.value.postcode;
  if (addressForm.value.countryId) insuranceForm.value.address_country = addressForm.value.countryId;
  if (addressForm.value.zoneId) insuranceForm.value.address_province = addressForm.value.zoneId;
}

function addressIncomplete(form) {
  return !form.address1.trim()
    || !form.city.trim()
    || !form.postcode.trim()
    || !toPositiveInt(form.countryId)
    || !toPositiveInt(form.zoneId);
}

function buildAddressPayload(form, contact, zones) {
  const fullName = String((contact && contact.name) || '').trim();
  const parts = fullName.split(/\s+/).filter(Boolean);
  return {
    firstname: parts[0] || '',
    lastname: parts.slice(1).join(' '),
    address1: form.address1.trim(),
    city: form.city.trim(),
    postcode: form.postcode.trim(),
    contact: String((contact && (contact.contact || contact.phone)) || '').trim(),
    state: selectedStateName(form, zones),
    country_id: toPositiveInt(form.countryId, PH_COUNTRY_ID),
    zone_id: toPositiveInt(form.zoneId)
  };
}

async function loadBillingZones(countryId, options = {}) {
  if (!options.keepZone) addressForm.value.zoneId = 0;
  billingZones.value = await fetchZones(countryId);
}

async function loadShippingZones(countryId, options = {}) {
  if (!options.keepZone) shippingForm.value.zoneId = 0;
  shippingZones.value = await fetchZones(countryId);
}

function applyPendingZone(form, zones, hintKey) {
  const resolved = findZoneId(zones, form.zoneId || pendingZoneHint[hintKey]);
  if (resolved) form.zoneId = resolved;
  pendingZoneHint[hintKey] = '';
}

async function hydrateAddressSelects() {
  if (!addressForm.value.countryId) addressForm.value.countryId = PH_COUNTRY_ID;
  await loadBillingZones(addressForm.value.countryId, { keepZone: true });
  applyPendingZone(addressForm.value, billingZones.value, 'billing');

  if (!sameShippingAsBilling.value) {
    if (!shippingForm.value.countryId) shippingForm.value.countryId = addressForm.value.countryId;
    await loadShippingZones(shippingForm.value.countryId, { keepZone: true });
    applyPendingZone(shippingForm.value, shippingZones.value, 'shipping');
  }
}

async function onBillingCountryChange() {
  await loadBillingZones(addressForm.value.countryId);
}

async function onShippingCountryChange() {
  await loadShippingZones(shippingForm.value.countryId);
}

function applyPaymentInitiate(props) {
  if (!props || props.type !== 'payment_initiate_props') return;
  paymentInitiate.value = { ...props };
  const contact = props.contact;
  if (contact && contact.email) insuranceForm.value.email = contact.email;
  if (contact && contact.name) {
    const parts = contact.name.split(' ');
    insuranceForm.value.fname = parts[0] || '';
    insuranceForm.value.lname = parts.slice(1).join(' ');
  }
  if (contact && contact.contact) insuranceForm.value.contact = contact.contact;

  fillAddressIfEmpty(addressForm.value, mapIncomingAddress(contact && contact.billingAddress), 'billing');
  fillAddressIfEmpty(addressForm.value, mapIncomingAddress(contact && contact.shippingAddress), 'billing');
  fillAddressIfEmpty(addressForm.value, loadLoggedInUserAddress(), 'billing');

  if (contact && contact.sameShippingAsBilling === false) {
    sameShippingAsBilling.value = false;
  }
  fillAddressIfEmpty(shippingForm.value, mapIncomingAddress(contact && contact.shippingAddress), 'shipping');
  syncAddressToInsurance();

  if (props.productDetails && props.productDetails.length) {
    productDetails.value = props.productDetails;
    hasInsuranceCoverage.value = props.productDetails.some(
      (p) => p && p.name === 'Bronze Coverage'
    );
  }
}

async function loadOrderData() {
  const pi = paymentInitiate.value;
  if (!pi || !pi.orderId || !locationId.value) return;
  try {
    const { data } = await fetchGhlPaymentOrderData({
      orderId: pi.orderId,
      locationId: locationId.value
    });
    if (data && data.productDetails) {
      productDetails.value = data.productDetails;
    }
    if (data && data.inclusions) {
      hasInsuranceCoverage.value = Boolean(data.inclusions.hasInsuranceCoverage);
    }
  } catch (err) {
    console.warn('orderData fetch failed', err);
  }
}

function parseParentPayload(data) {
  if (!data) return null;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch (err) {
      return null;
    }
  }
  if (typeof data === 'object') return data;
  return null;
}

function stopReadyHandshake() {
  if (readyIntervalId) {
    clearInterval(readyIntervalId);
    readyIntervalId = null;
  }
}

function postCustomProviderReady() {
  if (!window.parent || window.parent === window) return;
  window.parent.postMessage(READY_PAYLOAD, '*');
  window.parent.postMessage(JSON.stringify(READY_PAYLOAD), '*');
}

function startReadyHandshake() {
  if (isTerminalStatus.value || paymentInitiate.value) return;
  if (!window.parent || window.parent === window) return;

  let attempts = 0;
  postCustomProviderReady();
  readyIntervalId = setInterval(() => {
    if (paymentInitiate.value || attempts >= READY_MAX_ATTEMPTS) {
      stopReadyHandshake();
      return;
    }
    attempts += 1;
    postCustomProviderReady();
  }, READY_RETRY_MS);
}

function onParentMessage(event) {
  console.log("event")
  console.log(event)
  const payload = parseParentPayload(event && event.data);
  if (!payload) return;
  if (payload.type === 'payment_initiate_props') {
    applyPaymentInitiate(payload);
    stopReadyHandshake();
    hydrateAddressSelects();
    loadOrderData();
  }
  if (payload.paymentInitiate) {
    applyPaymentInitiate(payload.paymentInitiate);
    stopReadyHandshake();
    hydrateAddressSelects();
    loadOrderData();
  }
}

function redirectToPayDollar(gateway) {
  const result = performPayDollarRedirect(gateway);
  if (!result.ok) {
    errorMessage.value = result.error;
  }
}

watch(
  () => [
    addressForm.value.address1,
    addressForm.value.city,
    addressForm.value.postcode,
    addressForm.value.countryId,
    addressForm.value.zoneId
  ],
  () => {
    syncAddressToInsurance();
  }
);

watch(
  () => sameShippingAsBilling.value,
  async (same) => {
    if (same) return;
    if (!shippingForm.value.countryId) {
      shippingForm.value.countryId = addressForm.value.countryId || PH_COUNTRY_ID;
    }
    if (!shippingZones.value.length) {
      await loadShippingZones(shippingForm.value.countryId, { keepZone: true });
      applyPendingZone(shippingForm.value, shippingZones.value, 'shipping');
    }
  }
);

async function submitPayment() {
  if (!paymentInitiate.value) {
    errorMessage.value = 'Payment session not ready';
    return;
  }

  if (addressIncomplete(addressForm.value)) {
    errorMessage.value = 'Street address, city, state, and postal code are required';
    return;
  }

  if (!sameShippingAsBilling.value && addressIncomplete(shippingForm.value)) {
    errorMessage.value = 'Shipping street address, city, state, and postal code are required';
    return;
  }

  submitting.value = true;
  errorMessage.value = '';

  try {
    const contact = paymentInitiate.value.contact || {};
    const billingAddress = buildAddressPayload(addressForm.value, contact, billingZones.value);
    const payload = {
      paymentInitiate: {
        ...paymentInitiate.value,
        productDetails: productDetails.value.length
          ? productDetails.value
          : paymentInitiate.value.productDetails,
        contact: {
          ...contact,
          billingAddress,
          shippingAddress: sameShippingAsBilling.value
            ? billingAddress
            : buildAddressPayload(shippingForm.value, contact, shippingZones.value),
          sameShippingAsBilling: sameShippingAsBilling.value
        }
      }
    };

    if (hasInsuranceCoverage.value) {
      syncAddressToInsurance();
      payload.insuranceData = { ...insuranceForm.value };
    }

    const { data } = await submitGhlPaymentCheckout(payload);
    if (!data || !data.success) {
      errorMessage.value = (data && (data.message || data.error)) || 'Payment could not be started';
      return;
    }

    redirectToPayDollar(data.gateway);
  } catch (err) {
    const errResponse = err && err.response;
    const errData = errResponse && errResponse.data;
    errorMessage.value = (errData && errData.message) || err.message || 'Payment request failed';
  } finally {
    submitting.value = false;
  }
}

onMounted(async () => {
  paymentStatus.value = String(route.query.status || '');
  countries.value = await fetchCountries();

  applyPaymentInitiate(JSON.parse(
    "{\"type\":\"payment_initiate_props\",\"publishableKey\":\"iN64VJZ7SIEoVT14leD4_pubdev_56709c847a7a9d80f4782ea321135873\",\"amount\":3350,\"currency\":\"php\",\"mode\":\"payment\",\"productDetails\":[{\"_id\":\"698dd90616ffce73b2c17b60\",\"isTaxesEnabled\":false,\"taxes\":[],\"name\":\"Bronze Coverage\",\"qty\":1,\"productId\":\"698dd90616ffce73b2c17b60\",\"priceId\":\"698dd90616ffce53dbc17b65\",\"prices\":[{\"_id\":\"698dd90616ffce53dbc17b65\",\"name\":\"Bronze Coverage\",\"type\":\"one_time\",\"currency\":\"php\",\"amount\":3350,\"setupFee\":0,\"qty\":1,\"shippingOptions\":{\"weight\":{\"value\":null,\"unit\":\"kg\"},\"dimensions\":{\"length\":null,\"width\":null,\"height\":null,\"unit\":\"cm\"}}}]}],\"name\":\"\",\"description\":\"\",\"image\":\"\",\"contact\":{\"id\":\"JThKOKdDP5GPImfrtacZ\",\"name\":\"Jonas Carzon\",\"email\":\"jonzcarzon@gmail.com\",\"contact\":\"\",\"shippingAddress\":{\"city\":\"\",\"state\":\"\",\"country\":\"\",\"zipCode\":\"\",\"line1\":\"\"}},\"orderId\":\"6a83ee68af113e1bdd40d461\",\"invoiceId\":\"\",\"transactionId\":\"6a83ee691c44e31b530f5a7e\",\"locationId\":\"iN64VJZ7SIEoVT14leD4\",\"language\":\"en-US\"}"
  ));

  if (route.query.paymentInitiate) {
    try {
      applyPaymentInitiate(JSON.parse(route.query.paymentInitiate));
    } catch (err) {
      // ignore malformed test query param
    }
  }

  window.addEventListener('message', onParentMessage);
  startReadyHandshake();

  await hydrateAddressSelects();

  if (paymentInitiate.value) {
    await loadOrderData();
  }

  loading.value = false;
});

onUnmounted(() => {
  stopReadyHandshake();
  window.removeEventListener('message', onParentMessage);
});
</script>

<template>
  <div class="agg-payment-capture">
    <div v-if="isTerminalStatus" class="status-panel">
      <h2 v-if="paymentStatus === 'success'">Payment submitted</h2>
      <h2 v-else-if="paymentStatus === 'fail'">Payment failed</h2>
      <h2 v-else>Payment cancelled</h2>
      <p>You may close this window.</p>
    </div>

    <div v-else-if="loading" class="status-panel">
      <p>Loading checkout...</p>
    </div>

    <div v-else class="checkout-panel">
      <header class="checkout-header">
        <h1>Complete Payment</h1>
        <p v-if="paymentInitiate">Total: {{ formattedAmount }}</p>
      </header>

      <section v-if="productDetails.length" class="order-summary">
        <h2>Order Summary</h2>
        <ul>
          <li v-for="item in productDetails" :key="item.id || item.name">
            <span>{{ item.name }}</span>
            <span v-if="hasProductPrice(item)">{{ formatProductPrice(item) }}</span>
          </li>
        </ul>
      </section>

      <section v-if="paymentContact" class="contact-summary">
        <h2>Contact</h2>
        <p>{{ paymentContact.name }}</p>
        <p>{{ paymentContact.email }}</p>
      </section>

      <section class="address-form">
        <h2>Billing address</h2>
        <div class="form-grid">
          <label>Street address<input v-model="addressForm.address1" type="text" autocomplete="street-address" required /></label>
          <label>City<input v-model="addressForm.city" type="text" autocomplete="address-level2" required /></label>
          <label>Postal code<input v-model="addressForm.postcode" type="text" autocomplete="postal-code" required /></label>
          <label>
            Country
            <select v-model.number="addressForm.countryId" autocomplete="country" required @change="onBillingCountryChange">
              <option
                v-for="country in countries"
                :key="country.country_id"
                :value="Number(country.country_id)"
              >
                {{ country.name }}
              </option>
            </select>
          </label>
          <label>
            State / Province
            <select v-model.number="addressForm.zoneId" autocomplete="address-level1" required>
              <option :value="0">Select region / state</option>
              <option
                v-for="zone in billingZones"
                :key="zone.zone_id"
                :value="Number(zone.zone_id)"
              >
                {{ zone.name }}
              </option>
            </select>
          </label>
        </div>
        <label class="same-shipping">
          <input v-model="sameShippingAsBilling" type="checkbox" />
          <span>Shipping address is the same as billing</span>
        </label>
        <template v-if="!sameShippingAsBilling">
          <h2>Shipping address</h2>
          <div class="form-grid">
            <label>Street address<input v-model="shippingForm.address1" type="text" autocomplete="shipping street-address" required /></label>
            <label>City<input v-model="shippingForm.city" type="text" autocomplete="shipping address-level2" required /></label>
            <label>Postal code<input v-model="shippingForm.postcode" type="text" autocomplete="shipping postal-code" required /></label>
            <label>
              Country
              <select v-model.number="shippingForm.countryId" autocomplete="shipping country" required @change="onShippingCountryChange">
                <option
                  v-for="country in countries"
                  :key="`ship-${country.country_id}`"
                  :value="Number(country.country_id)"
                >
                  {{ country.name }}
                </option>
              </select>
            </label>
            <label>
              State / Province
              <select v-model.number="shippingForm.zoneId" autocomplete="shipping address-level1" required>
                <option :value="0">Select region / state</option>
                <option
                  v-for="zone in shippingZones"
                  :key="`ship-${zone.zone_id}`"
                  :value="Number(zone.zone_id)"
                >
                  {{ zone.name }}
                </option>
              </select>
            </label>
          </div>
        </template>
      </section>

      <section v-if="hasInsuranceCoverage" class="insurance-form">
        <h2>Bronze Coverage Details</h2>
        <div class="form-grid">
          <label>First name<input v-model="insuranceForm.fname" type="text" required /></label>
          <label>Middle name<input v-model="insuranceForm.mname" type="text" /></label>
          <label>Last name<input v-model="insuranceForm.lname" type="text" required /></label>
          <label>Date of birth<input v-model="insuranceForm.dob" type="date" required /></label>
          <label>Mobile<input v-model="insuranceForm.contact" type="tel" required /></label>
          <label>Email<input v-model="insuranceForm.email" type="email" required /></label>
          <label>Nationality<input v-model="insuranceForm.nationality" type="text" /></label>
        </div>

        <h3>Beneficiary</h3>
        <div class="form-grid">
          <label>First name<input v-model="insuranceForm.benefFname" type="text" required /></label>
          <label>Middle name<input v-model="insuranceForm.benefMname" type="text" /></label>
          <label>Last name<input v-model="insuranceForm.benefLname" type="text" required /></label>
          <label>Date of birth<input v-model="insuranceForm.benefDob" type="date" required /></label>
          <label>Contact<input v-model="insuranceForm.benefContact" type="tel" required /></label>
          <label>Relationship<input v-model="insuranceForm.benefRelationship" type="text" /></label>
        </div>
      </section>

      <p v-if="errorMessage" class="error">{{ errorMessage }}</p>

      <button
        class="pay-button"
        type="button"
        :disabled="submitting || !paymentInitiate"
        @click="submitPayment"
      >
        {{ submitting ? 'Processing...' : 'Pay with PayDollar' }}
      </button>

      <p v-if="!paymentInitiate" class="hint">
        Waiting for payment details from AI 24/7 checkout...
      </p>
    </div>
  </div>
</template>

<style scoped>
.agg-payment-capture {
  font-family: system-ui, -apple-system, sans-serif;
  padding: 1.5rem;
  max-width: 720px;
  margin: 0 auto;
  color: #1a1a1a;
}

.checkout-header h1 {
  margin: 0 0 0.25rem;
  font-size: 1.5rem;
}

.order-summary,
.contact-summary,
.address-form,
.insurance-form,
.status-panel,
.checkout-panel {
  margin-top: 1rem;
}

.same-shipping {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.85rem 0 0;
  font-size: 0.9rem;
}

.order-summary ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.order-summary li {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e5e5e5;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem;
}

label {
  display: flex;
  flex-direction: column;
  font-size: 0.85rem;
  gap: 0.25rem;
}

input,
select {
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.pay-button {
  margin-top: 1.25rem;
  width: 100%;
  padding: 0.85rem 1rem;
  border: none;
  border-radius: 6px;
  background: #0d6efd;
  color: #fff;
  font-size: 1rem;
  cursor: pointer;
}

.pay-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error {
  color: #b00020;
}

.hint {
  color: #666;
  font-size: 0.9rem;
}
</style>
